/**
 * Time Attack Matching Engine
 * 
 * Implements the Puyo Puyo-style matching logic for Time Attack mode.
 * Similar to Endless mode but with increasing score multipliers.
 */

import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { Grid, MatchGroup } from '../Grid'
import { GameBalanceManager } from '../GameBalanceManager'

export class TimeAttackMatchingEngine extends BaseMatchingEngine {
  private scoreMultiplier: number = 1.0

  constructor(grid: Grid, balanceManager?: GameBalanceManager) {
    super(grid, balanceManager)
  }

  /**
   * Processes all matches and cascades (Puyo Puyo style)
   * Returns the complete result after all chains resolve
   */
  public processMatches(): MatchResult {
    let totalScore = 0
    let totalEngraftment = 0
    let chainLevel = 0
    const allMatches: MatchGroup[] = []
    const allClearedCancer: any[] = [] // Empty in Puyo mode

    // Process cascades until no more matches are found
    while (true) {
      // Find current matches (4+ connected same-color blobs)
      const matches = this.grid.findMatches()

      if (matches.length === 0) {
        break // No more matches, cascade complete
      }

      // Process this chain level
      chainLevel++
      allMatches.push(...matches)

      // Calculate score for this chain level
      const chainResult = this.calculateChainScore(matches, chainLevel)
      totalScore += chainResult.score
      totalEngraftment += chainResult.engraftment

      // Mark cells for deletion
      for (const match of matches) {
        this.grid.markCellsForDeletion(match.cells)
      }

      // Remove matched cells and apply gravity
      this.grid.removeMarkedCells()
      this.grid.applyGravity()

      // Animation timing is coordinated by game loop
    }

    // Increase score multiplier with each successful match
    if (allMatches.length > 0) {
      this.increaseMultiplier()
    }

    const totalCellsCleared = allMatches.reduce(
      (sum, match) => sum + match.size,
      0
    ) + allClearedCancer.length

    return {
      matches: allMatches,
      clearedCancer: allClearedCancer,
      totalCellsCleared,
      chainLevel,
      scoreGained: Math.floor(totalScore * this.scoreMultiplier),
      engraftmentGained: totalEngraftment
    }
  }

  /**
   * Calculates score and engraftment for matches at a chain level
   */
  protected calculateChainScore(
    matches: MatchGroup[],
    chainLevel: number
  ): { score: number; engraftment: number } {
    let score = 0
    let engraftment = 0
    const multiplier = this.getChainMultiplier(chainLevel)

    for (const match of matches) {
      // Base score calculation
      const baseScore = this.calculateMatchScore(match, chainLevel)
      const matchScore = Math.floor(baseScore * multiplier)
      score += matchScore

      // Engraftment calculation
      const baseEngraftment = this.calculateMatchEngraftment(match)
      const matchEngraftment = Math.floor(baseEngraftment * (multiplier * 0.5))
      engraftment += matchEngraftment
    }

    return { score, engraftment }
  }

  /**
   * Calculates base score for a single match (Puyo-style scoring)
   */
  private calculateMatchScore(match: MatchGroup, chainLevel: number = 0): number {
    // Simplified Puyo scoring: base score is number of cells cleared
    let baseScore = match.size * 10

    // Size bonus: larger matches get exponentially more points
    if (match.size >= 5) {
      baseScore += (match.size - 4) * 20
    }
    if (match.size >= 8) {
      baseScore += (match.size - 7) * 40
    }

    return baseScore
  }

  /**
   * Calculates engraftment value for a match
   */
  private calculateMatchEngraftment(match: MatchGroup): number {
    // In Time Attack mode, engraftment is based on number of cells cleared
    return match.size
  }

  /**
   * Increase the score multiplier with each successful match
   */
  private increaseMultiplier(): void {
    // Increase multiplier by 0.1x per match, capped at 5.0x
    this.scoreMultiplier = Math.min(this.scoreMultiplier + 0.1, 5.0)
  }

  /**
   * Reset the score multiplier (e.g., when time runs out or no matches)
   */
  public resetMultiplier(): void {
    this.scoreMultiplier = 1.0
  }

  /**
   * Get current score multiplier
   */
  public getScoreMultiplier(): number {
    return this.scoreMultiplier
  }

  /**
   * Checks for immediate matches without processing them
   */
  public hasMatches(): boolean {
    const matches = this.grid.findMatches()
    return matches.length > 0
  }

  /**
   * Gets potential matches for preview/hint system
   */
  public getPotentialMatches(): MatchGroup[] {
    return this.grid.findMatches()
  }
}
